import React, { useEffect } from "react";

import { useAnalytics } from "@common/analytics/useAnalytics";
import { DetailsBar } from "@common/components/DetailsBar/DetailsBar";
import * as Icon from "@common/components/DetailsPane/icons";
import Heading from "@common/components/Heading/Heading";
import InputWithTags from "@common/components/Input/InputWithTags/InputWithTags";
import { RowType } from "@common/components/MasterTable/models/MasterTableModel";
import { ErrorStateView } from "@common/components/PageStateView/components/ErrorStateView";
import PageWrap from "@common/components/PageWrap/PageWrap";
import { Spinner } from "@common/components/Spinner/Spinner";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { useCustomElementInjection } from "@common/hooks/useCustomElementInjection";
import identifiers from "@common/util/identifiers.json";
import useCommonTagsApiConsumer from "@main/consumers/useCommonTagsApiConsumer";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import NavigationCustomElement from "@main/layouts/components/PageNavigation/NavigationCustomElement/NavigationCustomElement";
import { ELEMENT_CONTAINER_ID, NAV_LIST } from "@main/layouts/components/PageNavigation/PageNavigation";
import LinkedTicketsTable from "@main/pages/shared/components/LinkedTicketsTable/LinkedTicketsTable";
import MessageContainer from "@main/pages/shared/components/Messages/MessageContainer";
import { TicketContainer } from "@main/pages/shared/components/TicketDetails/TicketDetailsContainer.styles";
import { TicketInfoContainer } from "@main/pages/shared/components/TicketDetails/TicketInfoContainer";
import { TicketKeyCopy } from "@main/pages/shared/components/TicketKey/TicketKey";
import * as Styled from "@main/pages/UserFeedback/views/UserFeedback.styles";
import { RoutePaths } from "@main/router/config/routePaths";
import { generatePath, useNavigate, useParams } from "react-router-dom";

import CrashesPageNavigation from "../components/PageNavigation/CrashesPageNavigation";
import CrashReportPageNavigationContent from "../components/PageNavigation/CrashReport/CrashReportPageNavigationContent";
import useCrashOverviewApiConsumer from "../consumers/useCrashOverviewApiConsumer";

export default function CrashOverviewPageView() {
  const { sendEvent } = useAnalytics();

  useEffect(() => {
    sendEvent("page_view", { page_title: "Crash group details" });
  }, [sendEvent]);

  const { selectedWorkspace, selectedApp } = useAppSelectionContext();
  const navigate = useNavigate();
  const params = useParams<{ workspaceSlug: string; appKey: string; crashReportKey: string }>();
  const consumer = useCrashOverviewApiConsumer({
    selectedWorkspaceId: selectedWorkspace?.id,
    selectedAppId: selectedApp?.id,
    crashReportKey: params.crashReportKey,
  });

  const { commonTags } = useCommonTagsApiConsumer({ selectedWorkspaceId: selectedWorkspace?.id });

  const CustomElement = (
    <NavigationCustomElement>
      <div>
        <Tooltip
          text={"See all crashes"}
          position={"bottom"}
        >
          <Styled.NavbarNavigation
            onClick={() =>
              navigate(
                generatePath(RoutePaths.CRASH_REPORTS, {
                  workspaceSlug: selectedWorkspace?.slug ?? null,
                  appKey: selectedApp?.key ?? null,
                }),
              )
            }
          >
            <Icon.SendIcon
              height={20}
              width={20}
            />
          </Styled.NavbarNavigation>
        </Tooltip>
      </div>
      <CrashesPageNavigation crashTitle={consumer.crashOverview ? consumer.crashOverview.title : ""}>
        <TicketKeyCopy
          ticketKey={consumer.crashOverview?.key?.toString() ?? ""}
          enableCopy={true}
          marginLeft={8}
        />
        <CrashReportPageNavigationContent
          crashReport={consumer.crashOverview}
          selectedAppKey={selectedApp?.key}
          selectedWorkspaceSlug={selectedWorkspace?.slug}
          navigate={navigate}
        />
      </CrashesPageNavigation>
    </NavigationCustomElement>
  );

  useCustomElementInjection({
    parentElementId: NAV_LIST,
    element: CustomElement,
    containerId: ELEMENT_CONTAINER_ID,
    tagName: "li",
  });

  if (consumer.error) {
    return (
      <ErrorStateView
        title="There was an error loading ticket. Try refreshing."
        subtitle="The link you followed may be broken, or the ticket may have been removed."
      />
    );
  }

  if (!consumer.crashOverview) {
    return <Spinner />;
  }

  return (
    <PageWrap>
      <TicketContainer>
        <TicketInfoContainer>
          <Heading
            as="h2"
            heading2
            testId={identifiers["crash.reports.details.header"]}
            fontWeight="bold"
          >
            {consumer.crashOverview?.title}
          </Heading>
          <Heading
            as="h4"
            heading4
          >
            {consumer.crashOverview?.subtitle}
          </Heading>

          <DetailsBar
            details={consumer.crashOverview}
            changeAssignee={consumer.changeAssignee}
            changePriority={consumer.changePriority}
            changeStatus={consumer.changeStatus}
            deleteTicket={consumer.deleteCrashReport}
            showLock
            type={RowType.CRASH_REPORT}
            onMergeAction={consumer.onMergeAction}
            clearMerge={consumer.onUnMergeAction}
          />

          <LinkedTicketsTable linkedTickets={consumer.linkedIssues} />

          <InputWithTags
            selectedTags={consumer.crashOverview.tags}
            commonTags={commonTags}
            onAddTag={consumer.createCrashReportTag}
            onRemoveTag={consumer.deleteCrashReportTag}
          />

          <MessageContainer activity={consumer.crashActivity} />
        </TicketInfoContainer>
      </TicketContainer>
    </PageWrap>
  );
}
